import { FunctionComponent, useRef, useState } from 'react';
import { Period } from '@spike/model';
import ExportCSV from 'components/UI/ExportCSV/ExportCSV';
import { DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import { useNonInitialEffect } from '@versiondos/hooks';
import ApiClient from 'api';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import { showErrorThunk } from '@spike/notifications-action';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { ApiClientWrapper } from '@spike/api-client';
import StaffSelector from './StaffSelector/StaffSelector';
import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import styles from './Header.module.scss';
import { DateRangePickerOption } from 'components/UI/V3/DateRangePicker/DateRangePicker';

interface Body {
    conditions: Conditions;
}

interface Conditions {
    deceased: boolean;
    deleted: boolean;
    active: boolean;
}

interface HeaderProps {
    period: Period | null;
    className?: string;
    onChangePeriod: (period: Period | null, allTime: boolean) => void;
    exportFile: {
        name: string;
        url?: string;
        body?: Body;
        apiClientWrapper?: ApiClientWrapper;
        dataInMemory?: boolean;
        dataToExport?: string[][];
        headers?: string[];
        fetchData?: () => Promise<{ headers: string[]; data: string[][] }>;
    };
    calendar: {
        optionSelected: string;
    };
    showStaffSelector?: boolean;
    selectedStaffIds?: Array<number>;
    onStaffChange?: (selectedStaffIds: Array<number> | undefined) => void;
    hideAllTime?: boolean;
    dateOptions?: Array<DateRangePickerOption>;
}

const backendFieldSeparator = ';';

export const Header: FunctionComponent<HeaderProps> = props => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const { todayOption, thisMonthOption, last30DaysOption, allTimeOption } = useDateRangeOptions();

    const apiClientWrapper = useApiClientWrapper();
    const csvLinkRef = useRef<any>(null);

    const [exporting, setExporting] = useState<{
        loading: boolean;
        headers: Array<string>;
        data: Array<Array<string>>;
    }>({ loading: false, headers: [], data: [] });

    useNonInitialEffect(() => {
        if (!exporting.loading) {
            csvLinkRef.current?.export();
        }
    }, [exporting.data]);

    const exportHandler = () => {
        setExporting(prev => ({
            ...prev,
            loading: true,
            headers: [],
            data: []
        }));

        if (props.exportFile?.fetchData) {
            props.exportFile
                .fetchData()
                .then(({ headers, data }) => {
                    setExporting(prev => ({ ...prev, loading: false, headers: [...headers], data: [...data] }));
                })
                .catch(() => {
                    dispatch(showErrorThunk('Error exporting.'));
                    setExporting(prev => ({ ...prev, loading: false }));
                });
        } else if (props.exportFile?.dataInMemory) {
            setExporting(prev => ({
                ...prev,
                loading: false,
                headers: [...props.exportFile.headers!],
                data: [...props.exportFile.dataToExport!]
            }));
        } else if (props.exportFile.url) {
            ApiClient.post<Array<string>>(props.exportFile?.url, props.exportFile?.body, apiClientWrapper.requestConfig)
                .then(response => {
                    const headers: Array<string> = response.data[0].split(backendFieldSeparator);

                    const data: Array<Array<string>> = response.data
                        .slice(1)
                        .map(row => row.split(backendFieldSeparator));

                    setExporting(prev => ({
                        ...prev,
                        loading: false,
                        headers: [...headers],
                        data: [...data]
                    }));
                })
                .catch(() => {
                    dispatch(showErrorThunk('Error exporting.'));
                    setExporting(prev => ({ ...prev, loading: false }));
                });
        }
    };

    const changePeriodHandler = (value: Period | null) => {
        if (value === null) {
            props.onChangePeriod?.(null, true);
        } else {
            props.onChangePeriod?.(
                {
                    from: value.from.clone(),
                    to: value.to.clone()
                },
                false
            );
        }
    };

    const dateRangeOptions = [
        todayOption,
        thisMonthOption,
        last30DaysOption,
        ...(props.hideAllTime ? [] : [allTimeOption])
    ];

    return (
        <div className={props.className}>
            <div className={styles.bar}>
                <div className={styles.leftSection}>
                    <DateRangePickerDropdown
                        timeZone={timeZone}
                        value={props.period ?? (props.hideAllTime ? { from: moment(), to: moment() } : null)}
                        onChange={changePeriodHandler}
                        options={props.dateOptions ? props.dateOptions : dateRangeOptions}
                        buttonProps={{
                            'variant': 'outlined',
                            'leftIcon': faCalendarDays,
                            'aria-label': 'Date range filter',
                            'className': styles.calendar
                        }}
                        dropdownProps={{ align: 'start' }}
                    />
                    {props.showStaffSelector && (
                        <div className={styles.staffSelector}>
                            <StaffSelector
                                selectedIds={props.selectedStaffIds}
                                onSelect={props.onStaffChange}
                            />
                        </div>
                    )}
                </div>
                <ExportCSV
                    headers={exporting.headers}
                    records={exporting.data}
                    filename={props.exportFile.name}
                    ref={csvLinkRef}
                    exporting={exporting.loading}
                    onClick={exportHandler}
                />
            </div>
        </div>
    );
};

export default Header;
